import { QueryObserverResult, useQueryClient } from "@tanstack/react-query";
import {
	signOut as amplifySignOut,
	AuthSession,
	fetchAuthSession,
	getCurrentUser,
} from "aws-amplify/auth";
import * as React from "react";
import { toast } from "ui-components";

import {
	GetCurrentAccountResults,
	useGetCurrentAccountCache,
} from "~auth/queries/useGetCurrentAccount";
import {
	GetCurrentUserResults,
	useGetCurrentUserCache,
} from "~auth/queries/useGetCurrentUser";
import { SUBSCRIPTION_TYPE } from "~auth/types/__generated/gql/graphql";
import { dayjs } from "~utils/libs/dayjs";

export interface AuthContextValue {
	isLoading: boolean;
	isAuthenticated: boolean;
	isTrialExpired: boolean;
	isAccountClosed: boolean;
	signOut: () => void;
	refreshSession: () => Promise<{
		session: AuthSession;
		currentUser: QueryObserverResult<GetCurrentUserResults>;
		currentAccount: QueryObserverResult<GetCurrentAccountResults>;
	}>;
}

export const AuthContext = React.createContext<AuthContextValue>({
	isLoading: true,
	isAuthenticated: false,
} as AuthContextValue);

export interface AuthProviderProps {
	children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const queryClient = useQueryClient();
	const { refetch: refetchCurrentUser } = useGetCurrentUserCache();
	const { refetch: refetchCurrentAccount } = useGetCurrentAccountCache();

	const [states, setStates] = React.useState<
		Pick<
			AuthContextValue,
			"isLoading" | "isAuthenticated" | "isTrialExpired" | "isAccountClosed"
		>
	>({
		isLoading: true,
		isAuthenticated: false,
		isTrialExpired: false,
		isAccountClosed: false,
	});

	const refreshSession = async () => {
		const [session, currentUser, currentAccount] = await Promise.all([
			fetchAuthSession(),
			refetchCurrentUser(),
			refetchCurrentAccount(),
		]);

		setStates((prev) => ({
			...prev,
			isAuthenticated:
				!!session?.tokens && !!currentUser.data && !!currentAccount.data,
			isTrialExpired:
				currentAccount?.data?.subscription.type === SUBSCRIPTION_TYPE.TRIAL &&
				dayjs(currentAccount.data.subscription.trialExpiresAt).isBefore(
					dayjs(),
				),
			isAccountClosed:
				currentAccount?.data?.subscription.type === SUBSCRIPTION_TYPE.CLOSED,
		}));

		return { session, currentUser, currentAccount };
	};

	const signOut = () => {
		toast.promise(
			async () => {
				await amplifySignOut();
				queryClient.clear();
				setStates((prev) => ({ ...prev, isAuthenticated: false }));
			},
			{
				loading: "Signing you out...",
				error: (e) => e.message ?? "Something went wrong!",
			},
		);
	};

	React.useEffect(() => {
		async function initialCheck() {
			setStates((prev) => ({ ...prev, isLoading: true }));

			const [isAuthenticated, currentUser, currentAccount] = await Promise.all([
				getCurrentUser()
					.then(() => true)
					.catch(() => false),
				refetchCurrentUser().catch(() => ({ data: undefined })),
				refetchCurrentAccount().catch(() => ({ data: undefined })),
			]);

			setStates({
				isLoading: false,
				isAuthenticated:
					isAuthenticated && !!currentUser.data && !!currentAccount.data,
				isTrialExpired:
					currentAccount?.data?.subscription.type === SUBSCRIPTION_TYPE.TRIAL &&
					dayjs(currentAccount.data.subscription.trialExpiresAt).isBefore(
						dayjs(),
					),
				isAccountClosed:
					currentAccount?.data?.subscription.type === SUBSCRIPTION_TYPE.CLOSED,
			});
		}

		void initialCheck();

		// eslint-disable-next-line react-hooks/exhaustive-deps -- Expected
	}, []);

	return (
		<AuthContext.Provider
			value={{
				...states,
				signOut,
				refreshSession,
			}}>
			{children}
		</AuthContext.Provider>
	);
};

export const AuthConsumer = AuthContext.Consumer;

export const useAuthContext = () => React.useContext(AuthContext);
